import { createTheme } from '@mui/material/styles';

const COLOR_PALETTES = {
    emerald: {
        primary: { main: '#10b981', light: '#34d399', dark: '#059669', contrastText: '#fff' },
        secondary: { main: '#0d9488', light: '#14b8a6', dark: '#0f766e', contrastText: '#fff' },
        background: { dark: '#022c22', light: '#ecfdf5' }
    },
    blue: {
        primary: { main: '#3b82f6', light: '#60a5fa', dark: '#2563eb', contrastText: '#fff' },
        secondary: { main: '#0ea5e9', light: '#38bdf8', dark: '#0284c7', contrastText: '#fff' },
        background: { dark: '#1e3a8a', light: '#eff6ff' }
    },
    purple: {
        primary: { main: '#8b5cf6', light: '#a78bfa', dark: '#7c3aed', contrastText: '#fff' },
        secondary: { main: '#d946ef', light: '#e879f9', dark: '#c026d3', contrastText: '#fff' },
        background: { dark: '#4c1d95', light: '#f3e8ff' }
    },
    orange: {
        primary: { main: '#f97316', light: '#fb923c', dark: '#ea580c', contrastText: '#fff' },
        secondary: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706', contrastText: '#fff' },
        background: { dark: '#7c2d12', light: '#fff7ed' }
    },
    rose: {
        primary: { main: '#f43f5e', light: '#fb7185', dark: '#e11d48', contrastText: '#fff' },
        secondary: { main: '#ec4899', light: '#f472b6', dark: '#db2777', contrastText: '#fff' },
        background: { dark: '#881337', light: '#fff1f2' }
    },
};

const spacing = 8;

const shape = {
    borderRadius: 12,
    borderRadiusSm: 8,
    borderRadiusLg: 16,
    borderRadiusXl: 24,
};

const ARCADE_COLORS = {
    sky: '#4cc9f0',
    cloud: '#f8fafc',
    sun: '#fbbf24',
    grass: '#34d399',
    lava: '#fb7185',
    brick: '#8b5cf6',
    ink: '#111827',
    inkSoft: '#24324a',
};

const panelShadow = (mode) => mode === 'dark'
    ? '0 18px 42px rgba(3, 7, 18, 0.42), 0 4px 0 rgba(15, 23, 42, 0.48)'
    : '0 18px 42px rgba(15, 23, 42, 0.12), 0 4px 0 rgba(148, 163, 184, 0.24)';

const softBorder = (mode) => mode === 'dark'
    ? '1px solid rgba(255, 255, 255, 0.12)'
    : '1px solid rgba(15, 23, 42, 0.08)';

const arcadeBackground = (mode, palette) => mode === 'dark'
    ? `
        radial-gradient(circle at top left, rgba(76, 201, 240, 0.2), transparent 28%),
        radial-gradient(circle at top right, rgba(251, 191, 36, 0.16), transparent 30%),
        linear-gradient(180deg, #0f172a 0%, ${palette.background.dark} 44%, #111827 100%)
    `
    : `
        radial-gradient(circle at top left, rgba(76, 201, 240, 0.18), transparent 28%),
        radial-gradient(circle at top right, rgba(251, 191, 36, 0.2), transparent 30%),
        linear-gradient(180deg, #dff6ff 0%, #f8fdff 36%, ${palette.background.light} 100%)
    `;

export const arcadeGradient = (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 56%, ${ARCADE_COLORS.sun} 100%)`;

export const panelStyle = (theme, options = {}) => {
    const {
        tint = 'primary',
        interactive = false,
        alpha = theme.palette.mode === 'dark' ? 0.82 : 0.9,
    } = options;

    const tintColor = theme.palette[tint]?.main || theme.palette.primary.main;
    const baseSurface = theme.palette.mode === 'dark'
        ? `linear-gradient(180deg, rgba(15, 23, 42, ${alpha}) 0%, rgba(30, 41, 59, ${alpha - 0.06}) 100%)`
        : `linear-gradient(180deg, rgba(255, 255, 255, ${alpha}) 0%, rgba(248, 250, 252, ${alpha - 0.08}) 100%)`;

    return {
        background: `
            linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0)),
            ${baseSurface}
        `,
        border: softBorder(theme.palette.mode),
        boxShadow: panelShadow(theme.palette.mode),
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${tintColor}${theme.palette.mode === 'dark' ? '20' : '16'} 0%, transparent 38%)`,
            pointerEvents: 'none',
        },
        ...(interactive ? {
            transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.palette.mode === 'dark'
                    ? '0 24px 48px rgba(3, 7, 18, 0.48), 0 4px 0 rgba(15, 23, 42, 0.48)'
                    : '0 24px 48px rgba(15, 23, 42, 0.16), 0 4px 0 rgba(148, 163, 184, 0.28)',
                borderColor: `${tintColor}55`,
            },
        } : {}),
    };
};

export const heroStyle = (theme) => ({
    ...panelStyle(theme, { tint: 'secondary', alpha: theme.palette.mode === 'dark' ? 0.88 : 0.94 }),
    background: `
        linear-gradient(135deg, ${theme.palette.primary.main}22 0%, transparent 36%),
        linear-gradient(225deg, ${ARCADE_COLORS.sun}22 0%, transparent 28%),
        ${theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.92) 0%, rgba(30, 41, 59, 0.86) 100%)'
            : 'linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 249, 255, 0.92) 100%)'
        }
    `,
});

const getDesignTokens = (mode, primaryColor = 'emerald') => {
    const palette = COLOR_PALETTES[primaryColor] || COLOR_PALETTES.emerald;

    return {
        spacing,
        shape,
        palette: {
            mode,
            primary: palette.primary,
            secondary: palette.secondary,
            background: {
                default: mode === 'dark' ? '#111827' : '#f9fafb',
                paper: mode === 'dark' ? '#1f2937' : '#ffffff',
            },
            text: {
                primary: mode === 'dark' ? '#f9fafb' : '#111827',
                secondary: mode === 'dark' ? '#9ca3af' : '#4b5563',
            },
            divider: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
        },
        typography: {
            fontFamily: [
                'Inter',
                '"Trebuchet MS"',
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
                '"Apple Color Emoji"',
                '"Segoe UI Emoji"',
                '"Segoe UI Symbol"',
            ].join(','),
            h1: { fontWeight: 800, letterSpacing: 0, lineHeight: 1.05 },
            h2: { fontWeight: 800, letterSpacing: 0, lineHeight: 1.08 },
            h3: { fontWeight: 700, letterSpacing: 0, lineHeight: 1.12 },
            h4: { fontWeight: 600 },
            h5: { fontWeight: 600 },
            h6: { fontWeight: 600 },
            button: { fontWeight: 600, textTransform: 'none' },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    html: {
                        backgroundColor: mode === 'dark' ? '#0f172a' : '#dff6ff',
                    },
                    body: {
                        transition: 'background-color 0.3s ease, color 0.3s ease',
                        background: arcadeBackground(mode, palette),
                        minHeight: '100vh',
                        backgroundAttachment: 'fixed',
                        backgroundColor: mode === 'dark' ? '#0f172a' : '#dff6ff',
                    },
                    '#root': {
                        minHeight: '100vh',
                    },
                    '::selection': {
                        backgroundColor: `${ARCADE_COLORS.sun}99`,
                        color: ARCADE_COLORS.ink,
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        borderRadius: shape.borderRadius,
                        border: softBorder(mode),
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: shape.borderRadiusSm,
                        padding: '9px 16px',
                        transition: 'all 0.2s ease-in-out',
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: 'transparent',
                        fontWeight: 700,
                    },
                    contained: {
                        boxShadow: mode === 'dark'
                            ? '0 8px 0 rgba(15, 23, 42, 0.36), 0 14px 24px rgba(15, 23, 42, 0.28)'
                            : '0 8px 0 rgba(148, 163, 184, 0.28), 0 14px 24px rgba(15, 23, 42, 0.14)',
                        '&:hover': {
                            boxShadow: mode === 'dark'
                                ? '0 10px 0 rgba(15, 23, 42, 0.4), 0 18px 30px rgba(15, 23, 42, 0.3)'
                                : '0 10px 0 rgba(148, 163, 184, 0.3), 0 18px 30px rgba(15, 23, 42, 0.16)',
                            transform: 'translateY(-2px)',
                        },
                    },
                    outlined: {
                        borderColor: mode === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(15,23,42,0.12)',
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: shape.borderRadiusLg,
                        boxShadow: panelShadow(mode),
                        border: softBorder(mode),
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: shape.borderRadiusSm,
                        fontWeight: 500,
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            borderRadius: shape.borderRadiusSm,
                            backgroundColor: mode === 'dark' ? 'rgba(15, 23, 42, 0.5)' : 'rgba(255, 255, 255, 0.9)',
                        },
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: shape.borderRadiusLg,
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        borderRadius: shape.borderRadiusSm,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                    },
                },
            },
        },
    };
};

export const getTheme = (mode, primaryColor) => createTheme(getDesignTokens(mode, primaryColor));

export const glassStyle = (theme) => ({
    ...panelStyle(theme),
    backdropFilter: 'blur(14px)',
});
